import React from "react";
import { apiGetTvFeed } from "@/lib/publicApi";
import { pageOf, ROTATE_MS, tataLetakTv, TV_HEADER_PX } from "@/lib/tvPaging";
import { Loader2, AlertTriangle, Clock, PlayCircle, CheckCircle2, PackageCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { TvKode } from "@/components/TvKode";
import * as W from "@/lib/waktu";

const REFRESH_MS = 12000;

const fmtClock = (iso) => {
  if (!iso) return "—";
  try {
    return W.jamDetik(iso);
  } catch {
    return iso;
  }
};

// owner 2026-10-07: a card carries the customer and a short summary (taller)
const CARD_PX = 128;
const tglPendek = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : W.tanggalSingkat(d);
};
const jam = (iso) => (iso ? W.jam(iso) : "");

const Column = ({ title, Icon, accent, items, total, lainnya, perPage, tick, testid }) => {
  const { items: shown, page, pageCount } = pageOf(items, perPage, tick);
  return (
    <div data-testid={testid} className={`flex flex-col rounded-2xl border ${accent.border} ${accent.bg} overflow-hidden`}>
      <div className={`flex items-center justify-between px-5 py-3 ${accent.head}`}>
        <div className="flex items-center gap-2">
          <Icon size={22} />
          <span className="font-judul text-2xl uppercase leading-none">{title}</span>
        </div>
        <span data-testid={`${testid}-count`} className="text-2xl font-bold tabular-nums">{total}</span>
      </div>
      <div className="flex-1 p-3 space-y-2 overflow-hidden">
        {shown.map((it, i) => (
          <div key={`${it.nomor_spk}-${page}-${i}`} data-testid={`tv-card-${it.nomor_spk}`} className="rounded-lg border border-slate-800 bg-white px-4 py-2.5 space-y-0.5 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="font-mono text-base font-bold text-soq-biru truncate">{it.nomor_spk}</p>
              <span className="shrink-0 rounded bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-200">{it.kategori}</span>
            </div>
            {it.pelanggan && <p data-testid="tv-card-pelanggan" className="text-lg font-bold text-slate-100 leading-tight truncate">{it.pelanggan}</p>}
            {it.ringkas && <p data-testid="tv-card-ringkas" className="text-sm text-slate-300 leading-snug line-clamp-2">{it.ringkas}</p>}
            {it.cs && <p data-testid="tv-card-cs" className="text-sm font-semibold text-slate-400 truncate">CS: {it.cs}</p>}
            <div className="flex items-center justify-between gap-2 text-xs font-mono text-slate-500">
              <span className="truncate">{it.status === "DIAMBIL"
                ? `${it.cara === "DIKIRIM" ? "Dikirim" : "Diambil"} ${jam(it.diambil_pada)}`
                : `${it.pos_nama || ""}${it.step ? ` · step ${it.step}` : ""}`}</span>
              {it.rencana_selesai && it.status !== "DIAMBIL" && <span className="shrink-0">target {tglPendek(it.rencana_selesai)}{it.rencana_selesai_jam ? ` ${it.rencana_selesai_jam}` : ""}</span>}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-500 py-8 text-lg">Tidak ada</p>}
      </div>
      {(pageCount > 1 || lainnya > 0) && (
        <div className="flex items-center justify-between px-5 py-2 text-sm font-mono bg-slate-800 text-slate-300">
          <span data-testid={`${testid}-page`}>{pageCount > 1 ? `Halaman ${page + 1}/${pageCount}` : ""}</span>
          {lainnya > 0 && <span data-testid={`${testid}-lainnya`} className="font-bold text-slate-100">+{lainnya} lainnya</span>}
        </div>
      )}
    </div>
  );
};

export default function TvDisplay() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [perluKode, setPerluKode] = React.useState(false);  // owner 2026-10-16: /tv locked with the TV code
  // F1-H: /tv?pos=KODE shows one production post; SELESAI = finished there today
  const pos = new URLSearchParams(window.location.search).get("pos") || "";
  const [tick, setTick] = React.useState(0);
  // owner 2026-10-07: landscape or portrait (vertical TV) — measured, not configured
  const [layar, setLayar] = React.useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  React.useEffect(() => {
    const onResize = () => setLayar({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    const id = setInterval(() => setTick((t) => t + 1), ROTATE_MS);
    return () => { window.removeEventListener("resize", onResize); clearInterval(id); };
  }, []);

  const jumlahKolom = data ? (data.diambil ? 4 : 3) : (pos ? 3 : 4);
  const tata = tataLetakTv(layar.w, layar.h, jumlahKolom, CARD_PX);
  const perPage = tata.perPage;
  const gridStyle = tata.potret
    ? { gridTemplateColumns: `repeat(${tata.kolomGrid}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${tata.baris}, minmax(0, 1fr))`,
        height: `calc(100vh - ${TV_HEADER_PX}px)` }
    : undefined;

  const load = React.useCallback(async () => {
    try {
      const d = await apiGetTvFeed(pos);
      setData(d);
      setError(null);
      setPerluKode(false);
    } catch (e) {
      if (e.response?.status === 401) { setPerluKode(true); return; }
      setError(e.response?.status === 404 ? `POS "${pos}" tidak ditemukan.` : "Gagal memuat data produksi. Mencoba lagi…");
    } finally {
      setLoading(false);
    }
  }, [pos]);

  React.useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (perluKode) return <TvKode judul="TV Antrean" onMasuk={() => { setPerluKode(false); load(); }} />;
  return (
    <div data-testid="tv-display" className="min-h-screen bg-slate-950 text-slate-100 p-5 sm:p-7">
      {/* TAMPILAN-SOQ tahap 3: royal blue band with orange slanted stripes; kept
          under TV_HEADER_PX (140) incl. its margin, which the portrait layout uses. */}
      <header className="relative overflow-hidden -mx-5 -mt-5 sm:-mx-7 sm:-mt-7 mb-5 bg-soq-biru px-5 sm:px-7 py-3 text-white flex flex-wrap items-center justify-between gap-3">
        <span aria-hidden className="absolute top-0 bottom-0 -right-8 w-24 bg-soq-oranye [transform:skewX(-14deg)]" />
        <span aria-hidden className="absolute top-0 bottom-0 right-24 w-4 bg-soq-oranye [transform:skewX(-14deg)]" />
        <div className="relative flex items-center gap-4">
          <h1 className="sr-only">SOQPRINT</h1>
          <Logo varian="putih" className="h-12 sm:h-14 w-auto" />
          <p data-testid="tv-subtitle" className="font-judul text-2xl sm:text-3xl uppercase leading-none text-white">{data?.subtitle || (pos ? `POS ${pos}` : "Monitoring Produksi")}</p>
        </div>
        <div className="relative text-right pr-28 sm:pr-36">
          <p className="text-sm text-[#DDE1FF] font-semibold uppercase tracking-wider">Update Terakhir</p>
          <p data-testid="tv-updated-at" className="text-2xl font-bold font-mono tabular-nums text-white">{data ? fmtClock(data.updated_at) : "—"}</p>
        </div>
      </header>

      {error && (
        <div data-testid="tv-error" className="mb-5 rounded-xl border border-red-500/40 bg-red-500/15 px-5 py-3 text-red-200 flex items-center gap-2">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {loading && !data ? (
        <div data-testid="tv-loading" className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="animate-spin mr-3" size={28} /> <span className="text-xl">Memuat…</span>
        </div>
      ) : (
        data && (
          <div data-testid="tv-grid" data-tata={tata.potret ? `potret-${tata.kolomGrid}x${tata.baris}` : "datar"} style={gridStyle}
            className={`grid gap-4 ${tata.potret ? "" : data.diambil ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
            <Column
              testid="tv-col-menunggu"
              title="MENUNGGU"
              Icon={Clock}
              items={data.menunggu}
              total={data.counts?.menunggu ?? data.menunggu.length}
              lainnya={data.lainnya?.menunggu ?? 0}
              perPage={perPage}
              tick={tick}
              accent={{ border: "border-slate-600", bg: "bg-slate-800/30", head: "bg-slate-700 text-slate-100" }}
            />
            <Column
              testid="tv-col-diproses"
              title="SEDANG DIPROSES"
              Icon={PlayCircle}
              items={data.diproses}
              total={data.counts?.diproses ?? data.diproses.length}
              lainnya={data.lainnya?.diproses ?? 0}
              perPage={perPage}
              tick={tick}
              accent={{ border: "border-amber-500", bg: "bg-amber-500/10", head: "bg-amber-500 text-slate-950" }}
            />
            <Column
              testid="tv-col-selesai"
              title={pos ? "SELESAI HARI INI" : "SIAP DIAMBIL"}
              Icon={CheckCircle2}
              items={data.selesai}
              total={data.counts?.selesai ?? data.selesai.length}
              lainnya={data.lainnya?.selesai ?? 0}
              perPage={perPage}
              tick={tick}
              accent={{ border: "border-emerald-600", bg: "bg-emerald-500/10", head: "bg-emerald-600 text-white" }}
            />
            {data.diambil && (
              <Column
                testid="tv-col-diambil"
                title="SUDAH DIAMBIL · HARI INI"
                Icon={PackageCheck}
                items={data.diambil}
                total={data.counts?.diambil ?? data.diambil.length}
                lainnya={data.lainnya?.diambil ?? 0}
                perPage={perPage}
                tick={tick}
                accent={{ border: "border-soq-biru", bg: "bg-sky-500/5", head: "bg-soq-biru text-white" }}
              />
            )}
          </div>
        )
      )}
    </div>
  );
}
